import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { EntityType } from '@/modules/entities/types/EntityType';
import { AugmentedUpdateEntityInput } from '@/types/schema';

import { useHideNoDispositionsBadgeMutation } from '../graphql/HideNoDispositionsBadge.generated';

export interface UseHideNoBeneficiaryDesignationsBadgeParams {
  entityId: string;
  entitySubtypeId: string;
  entityType: EntityType;
}

/**
 * Hook to hide the "No beneficiary designations" badge for retirement /
 * insurance accounts by stamping the account-level
 * `beneficiaryDesignationsReviewedAt` timestamp.
 *
 * Counterpart to {@link useHideNoDispositionsBadge}, which stamps
 * `reviewedAt` on disposition scenarios. BDG entities don't read from
 * scenarios, so we acknowledge the empty state at the account level
 * instead.
 */
export function useHideNoBeneficiaryDesignationsBadge({
  entityId,
  entitySubtypeId,
  entityType,
}: UseHideNoBeneficiaryDesignationsBadgeParams) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [hideBadgeMutation, { loading }] = useHideNoDispositionsBadgeMutation({
    onError: (error) => {
      showFeedback('Unable to hide badge. Please try again.');
      reportError('Error hiding no beneficiary designations badge', error);
    },
  });

  const hideNoBeneficiaryDesignationsBadge = useCallback(() => {
    const input = buildEntityInput(entityId, entitySubtypeId, entityType);
    if (!input) {
      reportError(
        'Cannot hide beneficiary-designations badge for unsupported entity type',
        new Error(`Unsupported entity type: ${entityType}`)
      );
      return;
    }
    return hideBadgeMutation({ variables: { input } });
  }, [entityId, entitySubtypeId, entityType, hideBadgeMutation, reportError]);

  return {
    hideNoBeneficiaryDesignationsBadge,
    loading,
  };
}

function buildEntityInput(
  entityId: string,
  entitySubtypeId: string,
  entityType: EntityType
): AugmentedUpdateEntityInput | null {
  const updateSubtypeInput = {
    id: entitySubtypeId,
    update: {
      beneficiaryDesignationsReviewedAt: new Date(),
    },
  };

  switch (entityType) {
    case 'retirement-account':
      return {
        id: entityId,
        update: {},
        updateRetirementPersonalAccount: updateSubtypeInput,
      };
    case 'insurance-account':
      return {
        id: entityId,
        update: {},
        updateInsurancePersonalAccount: updateSubtypeInput,
      };
    default:
      return null;
  }
}
